/**
 * Zone boundary geometry — one implementation for both judges.
 *
 * The drawing UI shows what an overlapping boundary will become in realtime,
 * and the server computes the same thing again at save; if those two used
 * different geometry, the map would promise a shape the API does not store. So
 * the ray casts, the segment tests and the clip all live here, in shared, and
 * both sides import them.
 *
 * Rings are arrays of [lat, lng], open (no repeated closing point) — the
 * closing edge is implied. Coordinates are treated as planar, which at zone
 * scale (a neighbourhood, not a hemisphere) is exact enough for "do these
 * overlap" and for comparing one piece of a clipped shape against another.
 *
 * ## The one dependency
 *
 * An overlapping boundary is CLIPPED to its neighbour's edge rather than
 * refused, and that needs a real polygon difference. Hand-rolling one was
 * considered and rejected: the hard case for every boolean-polygon algorithm
 * (Greiner-Hormann, Vatti, Martinez) is degenerate input — shared edges and
 * shared vertices — and here degeneracy is not an edge case but the NORMAL
 * result. Clipping to a neighbour PRODUCES a boundary that runs along that
 * neighbour, and the next redraw feeds it straight back in. `polygon-clipping`
 * is what turf.js uses, is pure JavaScript along with everything it pulls in
 * (`splaytree`, `robust-predicates`) — which apps/mobile's Metro bundle
 * requires, since shared is transpiled into the phone bundle from source — and
 * it survives that round trip byte-for-byte.
 */
import polygonClipping from 'polygon-clipping';

export type LatLng = [number, number];

/** A zone as the boundary judges need to see it: who it hangs under, and its ring. */
export type ZoneBoundaryRow = {
  id: string;
  parentId: string | null;
  name: string;
  boundary: LatLng[] | null;
  /** Ground inside the ring that is NOT the zone's — a cut-out, or a neighbour it was drawn around (2026-09-15). */
  holes?: LatLng[][] | null;
};

export function validRing(ring: LatLng[]): string | null {
  if (ring.length < 3) return 'A boundary needs at least three points.';
  for (const [lat, lng] of ring) {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return 'A boundary point is not a coordinate.';
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return 'A boundary point is off the map.';
  }
  if (ring.length > 500) return 'A boundary has at most 500 points.';
  // A self-crossing ring has no inside, so "overlap" would mean nothing.
  const n = ring.length;
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      // Adjacent edges share a vertex by construction; skip them (and the
      // first/last pair, which are adjacent through the implied closing edge).
      if (j === i || j === (i + 1) % n || (j + 1) % n === i) continue;
      if (segmentsIntersect(ring[i]!, ring[(i + 1) % n]!, ring[j]!, ring[(j + 1) % n]!)) {
        return 'The boundary crosses itself.';
      }
    }
  }
  return null;
}

const orient = (a: LatLng, b: LatLng, c: LatLng): number => {
  const v = (b[1] - a[1]) * (c[0] - a[0]) - (b[0] - a[0]) * (c[1] - a[1]);
  return v > 1e-12 ? 1 : v < -1e-12 ? -1 : 0;
};

const onSegment = (a: LatLng, b: LatLng, p: LatLng): boolean =>
  Math.min(a[0], b[0]) - 1e-12 <= p[0] &&
  p[0] <= Math.max(a[0], b[0]) + 1e-12 &&
  Math.min(a[1], b[1]) - 1e-12 <= p[1] &&
  p[1] <= Math.max(a[1], b[1]) + 1e-12;

export function segmentsIntersect(a: LatLng, b: LatLng, c: LatLng, d: LatLng): boolean {
  const o1 = orient(a, b, c);
  const o2 = orient(a, b, d);
  const o3 = orient(c, d, a);
  const o4 = orient(c, d, b);
  if (o1 !== o2 && o3 !== o4) return true;
  if (o1 === 0 && onSegment(a, b, c)) return true;
  if (o2 === 0 && onSegment(a, b, d)) return true;
  if (o3 === 0 && onSegment(c, d, a)) return true;
  if (o4 === 0 && onSegment(c, d, b)) return true;
  return false;
}

export function pointInPolygon(point: LatLng, ring: LatLng[]): boolean {
  const [y, x] = point;
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [yi, xi] = ring[i]!;
    const [yj, xj] = ring[j]!;
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/**
 * Shoelace area of a ring, in square degrees. Planar like the rest of this
 * module, and never shown to anybody: it exists to compare one piece of a
 * clipped shape against another and to tell a real overlap from a sliver.
 */
export function ringArea(ring: LatLng[]): number {
  if (ring.length < 3) return 0;
  let twice = 0;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    twice += (ring[j]![1] + ring[i]![1]) * (ring[j]![0] - ring[i]![0]);
  }
  return Math.abs(twice) / 2;
}

/**
 * Ground shared by less than this is not ground. One square degree is about
 * 1.24e10 m² at these latitudes, so 1e-10 deg² is roughly a square metre —
 * below the accuracy of anything anybody draws with a mouse, and above the
 * float dust a boolean operation can leave along a shared edge.
 */
const SHARED_GROUND_EPSILON = 1e-10;

/**
 * Below this share of what was drawn, the clip is worth calling out by size.
 *
 * Half: a boundary that lost a corner does not need a warning, and one that
 * came back as a third of itself is not what the operator thought they drew.
 */
const MOSTLY_CLIPPED_AWAY = 0.5;

/** Closed ring in polygon-clipping's shape; ours are open, its are not. */
const closeRing = (ring: LatLng[]): LatLng[] => [...ring, ring[0]!];

const openRing = (ring: readonly (readonly number[])[]): LatLng[] => {
  const out = ring.map((p) => [p[0]!, p[1]!] as LatLng);
  const first = out[0]!;
  const last = out[out.length - 1]!;
  if (out.length > 1 && first[0] === last[0] && first[1] === last[1]) out.pop();
  return out;
};

/** Bounding boxes that do not touch cannot share ground — and this runs per zone on every cursor move. */
function boundsApart(a: LatLng[], b: LatLng[]): boolean {
  const box = (ring: LatLng[]) => {
    let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
    for (const [lat, lng] of ring) {
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
      if (lng < minLng) minLng = lng;
      if (lng > maxLng) maxLng = lng;
    }
    return { minLat, maxLat, minLng, maxLng };
  };
  const x = box(a);
  const y = box(b);
  return x.maxLat < y.minLat || y.maxLat < x.minLat || x.maxLng < y.minLng || y.maxLng < x.minLng;
}

/** The old test: any edges cross, or either ring holds a point of the other. */
function ringsMeet(a: LatLng[], b: LatLng[]): boolean {
  for (let i = 0; i < a.length; i++) {
    for (let j = 0; j < b.length; j++) {
      if (segmentsIntersect(a[i]!, a[(i + 1) % a.length]!, b[j]!, b[(j + 1) % b.length]!)) {
        return true;
      }
    }
  }
  return pointInPolygon(a[0]!, b) || pointInPolygon(b[0]!, a);
}

/** How much ground two rings actually share, in square degrees. */
function sharedGround(a: LatLng[], b: LatLng[]): number {
  try {
    let area = 0;
    for (const piece of polygonClipping.intersection([closeRing(a)], [closeRing(b)])) {
      const [outer, ...holes] = piece;
      if (!outer) continue;
      area += ringArea(openRing(outer));
      for (const hole of holes) area -= ringArea(openRing(hole));
    }
    return area;
  } catch {
    // polygon-clipping can fail to close an output ring on pathological input.
    // Falling back to the old edge test errs towards "these overlap", which
    // refuses the save rather than quietly writing a boundary over a
    // neighbour's ground — the safe direction when the geometry is unknown.
    return ringsMeet(a, b) ? Infinity : 0;
  }
}

/**
 * Does `outer` hold the whole of `inner`?
 *
 * Measured, not inferred from vertices: a ring every one of whose corners sits
 * inside another can still bulge out between them. `inner` minus its shared
 * ground with `outer` being nothing is the definition that holds either way.
 */
function ringContains(outer: LatLng[], inner: LatLng[]): boolean {
  const area = ringArea(inner);
  if (area <= SHARED_GROUND_EPSILON) return false;
  return area - sharedGround(inner, outer) <= SHARED_GROUND_EPSILON;
}

/**
 * Overlap means SHARED GROUND, not a shared line.
 *
 * This used to be "any edges cross or either contains a point of the other",
 * which called two zones meeting along a fence an overlap — contradicting the
 * refusal's own words, "zones share borders, never ground". That was tolerable
 * while an overlap was simply refused. It is not tolerable now: a clipped
 * boundary runs ALONG its neighbour by construction, so the old test would
 * refuse the very shape the clip had just produced, and nothing clipped could
 * ever be saved or redrawn.
 */
export function polygonsOverlap(a: LatLng[], b: LatLng[]): boolean {
  if (a.length < 3 || b.length < 3) return false;
  if (boundsApart(a, b)) return false;
  return sharedGround(a, b) > SHARED_GROUND_EPSILON;
}

/**
 * The zones a drawing may touch at all — everything in the org except the zone
 * itself, its kin (ancestors and descendants) and a zone that already
 * surrounds it. `conflictingZones` is these filtered by overlap; the neighbour
 * moves of 2026-09-15 need the same list unfiltered, because a neighbour that
 * merely borders the released ground is not overlapping anything.
 */
export function eligibleNeighbours(
  zoneId: string | null,
  parentId: string | null,
  zones: ZoneBoundaryRow[],
): ZoneBoundaryRow[] {
  const byId = new Map(zones.map((z) => [z.id, z] as const));
  const ancestorsOf = (start: string | null): Set<string> => {
    const out = new Set<string>();
    let current = start;
    for (let hops = 0; current && hops < 10; hops++) {
      out.add(current);
      current = byId.get(current)?.parentId ?? null;
    }
    return out;
  };
  const related = (a: string, b: string): boolean =>
    ancestorsOf(a).has(b) || ancestorsOf(b).has(a);

  /**
   * A zone being CREATED has no id yet, so its ancestor exemption comes from
   * the parent it is about to hang under — without this, drawing a sub-zone
   * inside its parent was refused as an overlap, which is exactly the nesting
   * the tree exists for. (Found live: the first child boundary was refused.)
   */
  const newZoneAncestors = zoneId === null ? ancestorsOf(parentId) : null;

  /**
   * Zones that already SURROUND this one, which the platform itself allowed.
   *
   * A boundary is one ring and cannot hold a hole, so when somebody draws a
   * zone around an existing one the enclosed zone is deliberately not excluded
   * from it — `clipBoundary` says so in as many words and stores it anyway.
   * That leaves the enclosed zone sitting wholly inside its neighbour, which
   * the plain overlap test then reads as a total collision in the other
   * direction: every ring it could draw is inside that neighbour, so it clips
   * to nothing and every save is refused.
   *
   * Measured live: after "Kabete Ward" was drawn around "Kabete Estate" —
   * the sanctioned path, 201 with the notice — re-saving Estate's own stored
   * boundary UNCHANGED returned 400 "entirely inside Kabete Ward". The zone had
   * a child and, in the field, subscribers, and nothing an operator could do to
   * it got it back; the only remedy was elsewhere.
   *
   * So a zone that contains this one's CURRENT stored ring is exempt, exactly
   * as an ancestor is. The containment is a fact about the saved state rather
   * than about what is being drawn now, so this cannot be used to draw over a
   * neighbour: a third zone is not exempt, and a ring that leaves the
   * surrounding zone is judged against everything it then reaches.
   */
  const stored = zoneId === null ? null : (byId.get(zoneId)?.boundary ?? null);
  const surrounds =
    stored && stored.length >= 3
      ? new Set(
          zones
            .filter((z) => z.id !== zoneId && z.boundary && z.boundary.length >= 3)
            .filter((z) => ringContains(z.boundary!, stored))
            .map((z) => z.id),
        )
      : null;

  return zones
    .filter((z) => z.boundary && z.boundary.length >= 3)
    .filter((z) => z.id !== zoneId)
    .filter((z) => !(zoneId && related(zoneId, z.id)))
    .filter((z) => !newZoneAncestors?.has(z.id))
    .filter((z) => !surrounds?.has(z.id));
}

/**
 * Which zones a candidate boundary collides with. Ancestors and descendants
 * are exempt — a sub-zone inside its parent is nesting, not a violation —
 * and that is the same rule the zone tree itself already lives by.
 *
 * Getting this exemption wrong is not a cosmetic bug now that the answer feeds
 * the clip: a parent counted as a conflict would have its child subtracted out
 * of existence. Since 2026-09-15 a neighbour's holes count as not-its-ground:
 * a ring drawn inside a cut-out collides with nothing.
 */
export function conflictingZones(
  candidate: LatLng[],
  zoneId: string | null,
  parentId: string | null,
  zones: ZoneBoundaryRow[],
): ZoneBoundaryRow[] {
  const drawn: ZoneShape = { boundary: candidate, holes: [] };
  return eligibleNeighbours(zoneId, parentId, zones).filter((z) => shapesOverlap(drawn, shapeOf(z)!));
}

/** The same collision, named — what the operator is told they are up against. */
export function boundaryConflicts(
  candidate: LatLng[],
  zoneId: string | null,
  parentId: string | null,
  zones: ZoneBoundaryRow[],
): string[] {
  return conflictingZones(candidate, zoneId, parentId, zones).map((z) => z.name);
}

/** "Riverside", "Riverside and Hilltop", "Riverside, Hilltop and Karura". */
function nameList(names: string[]): string {
  /*
    Two zones may share a name — nothing constrains them to be unique — and the
    raw list then read "Pushed off Ngong and Ngong", which tells the operator
    two neighbours moved and gives them no way to tell which two, on the one
    screen where knowing which neighbour moved is the whole point. Counted
    instead: "2 zones called Ngong" is at least true and actionable.
  */
  const counts = new Map<string, number>();
  for (const name of names) counts.set(name, (counts.get(name) ?? 0) + 1);
  const parts = [...counts].map(([name, n]) => (n === 1 ? name : `${n} zones called ${name}`));
  if (parts.length <= 2) return parts.join(' and ');
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/** What a drawn boundary becomes once the zones it may not cover are taken out of it. */
export type BoundaryClip = {
  /** The ring to store, or null when the clip left nothing and `error` says why. */
  boundary: LatLng[] | null;
  /** Holes the clip left in it — a zone the ring was drawn around (2026-09-15). */
  holes: LatLng[][];
  /** Zones the boundary was pushed off. Empty means the drawn ring is stored exactly as drawn. */
  clippedFrom: string[];
  /**
   * Zones this boundary completely surrounds, whose ground is therefore STILL
   * inside it. `zones.boundary` is one ring and a ring cannot hold a hole.
   */
  covers: string[];
  /** Pieces thrown away because the clip cut the shape apart; the largest was kept. */
  droppedPieces: number;
  /** One sentence saying what changed, or null when nothing did. Both judges say these words. */
  notice: string | null;
  /** Why there is no boundary to store. Null whenever `boundary` is set. */
  error: string | null;
};

const clean = (boundary: LatLng[] | null, error: string | null): BoundaryClip => ({
  boundary,
  holes: [],
  clippedFrom: [],
  covers: [],
  droppedPieces: 0,
  notice: null,
  error,
});

/**
 * Push a drawn boundary off the ground it may not take, instead of refusing it.
 *
 * The operator's ask, in their words: "when creating a new zone and it overlaps
 * another, just push the new zone's boundary to the edge of the old boundary
 * instead of erroring out." So this is the candidate MINUS the union of the
 * zones it is not allowed to overlap — with three things the difference alone
 * cannot decide:
 *
 *  - the difference can come back in several pieces; the largest is kept and
 *    the operator is told the rest went, because they drew ground that is not
 *    in the saved zone and are entitled to know;
 *  - the difference can be a polygon with a HOLE, which one ring cannot store,
 *    so the outer ring is kept and the enclosed zone is named plainly;
 *  - the difference can be empty — a shape drawn wholly inside another zone —
 *    and that is still an error, naming the zone that swallowed it.
 *
 * Nothing in the way means the ring is returned untouched, which is what makes
 * this round-trip: re-saving an already-clipped boundary finds no shared ground
 * (it runs ALONG its neighbour, it does not cover it) and moves nothing.
 */
export function clipBoundary(
  candidate: LatLng[],
  zoneId: string | null,
  parentId: string | null,
  zones: ZoneBoundaryRow[],
): BoundaryClip {
  // A self-crossing ring has no inside, so a difference taken against it is
  // meaningless — refuse before the clipper ever sees it.
  const problem = validRing(candidate);
  if (problem) return clean(null, problem);

  const hits = conflictingZones(candidate, zoneId, parentId, zones);
  if (hits.length === 0) return clean(candidate, null);

  let pieces: ReturnType<typeof polygonClipping.difference>;
  try {
    pieces = polygonClipping.difference(
      [closeRing(candidate)],
      ...hits.map((z) => toPoly(shapeOf(z)!)),
    );
  } catch {
    // The clipper failed on this shape. Refusing is the honest outcome: the
    // alternative is storing a boundary over a neighbour's ground.
    return clean(
      null,
      `That boundary overlaps ${nameList(hits.map((z) => z.name))} and could not be clipped off ${hits.length === 1 ? 'it' : 'them'} — redraw it.`,
    );
  }

  if (pieces.length === 0) {
    return clean(
      null,
      `That boundary is entirely inside ${nameList(hits.map((z) => z.name))} — clipping it leaves no ground to save.`,
    );
  }

  // Largest by the ring that would actually be STORED, not by the true area of
  // the piece: a hole is not storable, so its ground stays inside the zone
  // either way, and ranking by anything else could drop the bigger shape.
  let best = pieces[0]!;
  let bestArea = ringArea(openRing(best[0]!));
  for (const piece of pieces.slice(1)) {
    const area = ringArea(openRing(piece[0]!));
    if (area > bestArea) {
      best = piece;
      bestArea = area;
    }
  }
  const kept = openRing(best[0]!);
  // A neighbour the ring was drawn AROUND is now a hole in it (2026-09-15) —
  // a zone can hold holes — so the ground stays that neighbour's.
  const keptHoles = best.slice(1).map((h) => openRing(h)).filter((h) => h.length >= 3);

  const spoiled = validRing(kept);
  if (spoiled) {
    // A clipped ring still has to obey the rules every other ring obeys — the
    // 500-point cap above all — or the next redraw would call the stored
    // boundary invalid.
    return clean(null, `Clipping that off ${nameList(hits.map((z) => z.name))} left a shape that cannot be stored: ${spoiled}`);
  }

  // A zone the kept SHAPE still covers — none, now that holes are kept; the
  // field stays so an older client reading `covers` sees an honest empty list.
  const keptShape: ZoneShape = { boundary: kept, holes: keptHoles };
  const covered = hits.filter((z) => shapesOverlap(keptShape, shapeOf(z)!));
  const coveredIds = new Set(covered.map((z) => z.id)); // by id, never by name — two zones may share one
  const covers = covered.map((z) => z.name);
  const clippedFrom = hits.filter((z) => !coveredIds.has(z.id)).map((z) => z.name);
  const droppedPieces = pieces.length - 1;

  const sentences: string[] = [];
  if (clippedFrom.length > 0) {
    sentences.push(
      `Pushed off ${nameList(clippedFrom)} — the boundary now runs along ${clippedFrom.length === 1 ? 'its edge' : 'their edges'}.`,
    );
  }
  /*
    HOW MUCH was taken, whenever it is most of what was drawn.
    
    "Pushed off Riverside — the boundary now runs along its edge" is a fine
    sentence for a one-metre correction and a lie for a clip that kept 0.09% of
    the shape. Measured: a zone drawn one hundred-thousandth of a degree outside
    Riverside's corner stored a 1.1 m by 1.1 km ribbon and got that identical
    reassuring sentence, because the only magnitude warning was gated on the
    shape SPLITTING and this clip yields exactly one piece. The map preview does
    not rescue it either — at that scale the difference is sub-pixel.

    Reported as a proportion rather than an area because square degrees mean
    nothing to anybody, and only past a threshold because a boundary tidied by a
    metre does not need a warning.
  */
  const drawnArea = ringArea(candidate);
  const keptFraction = drawnArea > 0 ? bestArea / drawnArea : 1;
  if (clippedFrom.length > 0 && keptFraction < MOSTLY_CLIPPED_AWAY) {
    sentences.push(
      `That took most of it: about ${Math.max(1, Math.round(100 - keptFraction * 100))}% of the shape you drew is not in this zone.`,
    );
  }
  if (droppedPieces > 0) {
    sentences.push(
      `That cut it into ${droppedPieces + 1} pieces and only the largest was kept, so ground you drew is not in this zone.`,
    );
  }
  if (covers.length > 0) {
    sentences.push(
      `${nameList(covers)} ${covers.length === 1 ? 'sits' : 'sit'} inside this boundary and ${covers.length === 1 ? 'is' : 'are'} NOT excluded from it.`,
    );
  }
  if (keptHoles.length > 0) {
    sentences.push(
      `${keptHoles.length === 1 ? 'The zone this ring surrounds is' : `The ${keptHoles.length} zones this ring surrounds are`} kept out of it as ${keptHoles.length === 1 ? 'a hole' : 'holes'}.`,
    );
  }

  return {
    // Nothing was pushed off and nothing was dropped means the difference
    // changed no ground, so the operator's own ring is what gets stored.
    boundary: clippedFrom.length === 0 && droppedPieces === 0 && keptHoles.length === 0 ? candidate : kept,
    holes: keptHoles,
    clippedFrom,
    covers,
    droppedPieces,
    notice: sentences.length > 0 ? sentences.join(' ') : null,
    error: null,
  };
}

// =============================================================================
// Shapes with holes, neighbours that move, and cut-outs (2026-09-15)
//
// The operator's words: "automove boundaries when drawing an area over another
// area", "automove a boundary towards a neighbouring area when the neighbouring
// area is being pulled away during redraw", and "a new type of redraw called
// cut out where the cut out area could be a new area or an empty space".
//
// Three ideas, one geometry. A zone is now a SHAPE — an outer ring and any
// number of holes — because a cut-out in the middle of a zone is a hole, and
// so is a neighbour a ring was drawn around. A redraw is authoritative: the
// ring the operator draws is what their zone becomes, and the neighbours
// adjust — PUSHED back off the ground the new ring covers, PULLED forward onto
// the ground the old ring held and the new one released, so a shared border
// stays shared and no gap opens. A cut-out takes a piece out of one zone and
// hands it back either as a hole or as a new zone with exactly that shape.
//
// `clipBoundary` above is the older rule — the DRAWN ring yields to its
// neighbours — kept as the "keep neighbours" mode. Both judges (the map's live
// preview and the API at save) run the same functions here.
// =============================================================================

/** A zone's ground: its outer ring, less its holes. Rings open, like everywhere else. */
export type ZoneShape = { boundary: LatLng[]; holes: LatLng[][] };

/** polygon-clipping's polygon: closed rings, outer first. */
type Poly = LatLng[][];

/** A zone row as a shape, or null where it has no ring worth the name. */
export function shapeOf(z: {
  boundary: LatLng[] | null | undefined;
  holes?: LatLng[][] | null | undefined;
}): ZoneShape | null {
  if (!z.boundary || z.boundary.length < 3) return null;
  return { boundary: z.boundary, holes: (z.holes ?? []).filter((h) => h.length >= 3) };
}

function toPoly(s: ZoneShape): Poly {
  return [closeRing(s.boundary), ...s.holes.map(closeRing)];
}

/** polygon-clipping's answer back as shapes, largest ground first. */
function fromPieces(pieces: readonly (readonly (readonly (readonly number[])[])[])[]): ZoneShape[] {
  const out: ZoneShape[] = [];
  for (const piece of pieces) {
    const [outer, ...holes] = piece;
    if (!outer) continue;
    const boundary = openRing(outer);
    if (boundary.length < 3) continue;
    out.push({ boundary, holes: holes.map((h) => openRing(h)).filter((h) => h.length >= 3) });
  }
  return out.sort((a, b) => shapeArea(b) - shapeArea(a));
}

/** Ground in the shape: the ring's area less its holes'. Square degrees, never shown. */
export function shapeArea(s: ZoneShape): number {
  return Math.max(0, ringArea(s.boundary) - s.holes.reduce((sum, h) => sum + ringArea(h), 0));
}

/** Inside the ring and outside every hole. */
export function pointInShape(point: LatLng, s: ZoneShape): boolean {
  return pointInPolygon(point, s.boundary) && !s.holes.some((h) => pointInPolygon(point, h));
}

function sharedGroundShapes(a: ZoneShape, b: ZoneShape): number {
  try {
    let area = 0;
    for (const piece of polygonClipping.intersection(toPoly(a), toPoly(b))) {
      const [outer, ...holes] = piece;
      if (!outer) continue;
      area += ringArea(openRing(outer));
      for (const hole of holes) area -= ringArea(openRing(hole));
    }
    return area;
  } catch {
    return ringsMeet(a.boundary, b.boundary) ? Infinity : 0;
  }
}

/** Shared GROUND, holes honoured: a ring drawn inside a neighbour's cut-out overlaps nothing. */
export function shapesOverlap(a: ZoneShape, b: ZoneShape): boolean {
  if (boundsApart(a.boundary, b.boundary)) return false;
  return sharedGroundShapes(a, b) > SHARED_GROUND_EPSILON;
}

/** Bordering: no ground shared, and their union is one piece. */
function shapesTouch(a: ZoneShape, b: ZoneShape): boolean {
  if (boundsApart(a.boundary, b.boundary)) return false;
  if (sharedGroundShapes(a, b) > SHARED_GROUND_EPSILON) return false;
  try {
    return polygonClipping.union(toPoly(a), toPoly(b)).length === 1;
  } catch {
    return false;
  }
}

/**
 * How much border two rings share — the collinear overlap of their edges, in
 * degrees. Decides WHICH neighbour takes released ground when more than one
 * borders it: the one it shared the longest fence with.
 */
function ringsSharedEdge(r1: LatLng[], r2: LatLng[]): number {
  let total = 0;
  for (let i = 0; i < r1.length; i++) {
    const a1 = r1[i]!;
    const a2 = r1[(i + 1) % r1.length]!;
    const dx = a2[0] - a1[0];
    const dy = a2[1] - a1[1];
    const len2 = dx * dx + dy * dy;
    if (len2 === 0) continue;
    for (let j = 0; j < r2.length; j++) {
      const b1 = r2[j]!;
      const b2 = r2[(j + 1) % r2.length]!;
      if (orient(a1, a2, b1) !== 0 || orient(a1, a2, b2) !== 0) continue;
      const t = (p: LatLng) => ((p[0] - a1[0]) * dx + (p[1] - a1[1]) * dy) / len2;
      const lo = Math.max(0, Math.min(t(b1), t(b2)));
      const hi = Math.min(1, Math.max(t(b1), t(b2)));
      if (hi > lo) total += (hi - lo) * Math.sqrt(len2);
    }
  }
  return total;
}

export function sharedEdgeLength(a: ZoneShape, b: ZoneShape): number {
  const ringsA = [a.boundary, ...a.holes];
  const ringsB = [b.boundary, ...b.holes];
  let total = 0;
  for (const ra of ringsA) for (const rb of ringsB) total += ringsSharedEdge(ra, rb);
  return total;
}

/** One neighbour after a redraw moved it. */
export type NeighbourMove = {
  id: string;
  name: string;
  boundary: LatLng[];
  holes: LatLng[][];
  change: 'pushed' | 'pulled' | 'pushed and pulled';
  /** Pieces the push cut the neighbour into and threw away; the largest was kept. */
  droppedPieces: number;
};

export type BoundaryRedraw = {
  /** The ring stored for the zone being drawn — the one drawn, exactly — or null with `error`. */
  boundary: LatLng[] | null;
  /** The zone's existing holes that the new ring still holds. */
  holes: LatLng[][];
  neighbours: NeighbourMove[];
  notice: string | null;
  error: string | null;
};

const redrawError = (error: string): BoundaryRedraw => ({ boundary: null, holes: [], neighbours: [], notice: null, error });

/**
 * A redraw that moves the neighbours instead of yielding to them.
 *
 * PUSH: every eligible neighbour that shares ground with the new ring loses
 * that ground (neighbour minus ring; the largest piece kept, like the clip).
 * A neighbour that would be left with nothing refuses the whole redraw — a
 * zone is deleted on purpose, never by drawing over it.
 *
 * PULL: the ground the OLD ring held and the new one does not is offered to
 * the neighbour that bordered it — the one sharing the longest edge with each
 * released piece — which grows to take it, so a fence that was shared stays
 * shared and no strip of nobody's ground opens between two zones. Released
 * ground that bordered no zone stays unassigned, and the notice says so.
 *
 * Holes the zone already has stay where the new ring still holds them.
 */
export function redrawWithNeighbours(
  previous: ZoneShape | null,
  candidate: LatLng[],
  zoneId: string | null,
  parentId: string | null,
  zones: ZoneBoundaryRow[],
): BoundaryRedraw {
  const problem = validRing(candidate);
  if (problem) return redrawError(problem);

  const drawn: ZoneShape = { boundary: candidate, holes: [] };
  const keptHoles = (previous?.holes ?? []).filter(
    (h) => ringArea(h) - sharedGroundShapes({ boundary: h, holes: [] }, drawn) <= SHARED_GROUND_EPSILON,
  );
  const next: ZoneShape = { boundary: candidate, holes: keptHoles };

  const eligible = eligibleNeighbours(zoneId, parentId, zones)
    .map((row) => ({ row, shape: shapeOf(row)! }))
    .filter((n) => n.shape !== null);
  const moves = new Map<string, NeighbourMove>();

  // --- push ------------------------------------------------------------------
  const pushed: string[] = [];
  const cutUp: string[] = [];
  for (const n of eligible) {
    if (!shapesOverlap(next, n.shape)) continue;
    let pieces: ZoneShape[];
    try {
      pieces = fromPieces(polygonClipping.difference(toPoly(n.shape), toPoly(next)));
    } catch {
      return redrawError(`That boundary overlaps ${n.row.name} and ${n.row.name} could not be pushed off it — redraw it.`);
    }
    if (pieces.length === 0 || shapeArea(pieces[0]!) <= SHARED_GROUND_EPSILON) {
      return redrawError(
        `That would leave nothing of ${n.row.name}. A zone is deleted on purpose, never by drawing over it — delete ${n.row.name} first, or draw around it.`,
      );
    }
    const kept = pieces[0]!;
    const spoiled = validRing(kept.boundary);
    if (spoiled) return redrawError(`Pushing ${n.row.name} back left it a shape that cannot be stored: ${spoiled}`);
    n.shape = kept;
    moves.set(n.row.id, {
      id: n.row.id,
      name: n.row.name,
      boundary: kept.boundary,
      holes: kept.holes,
      change: 'pushed',
      droppedPieces: pieces.length - 1,
    });
    pushed.push(n.row.name);
    if (pieces.length > 1) cutUp.push(n.row.name);
  }

  // --- pull ------------------------------------------------------------------
  const pulled: string[] = [];
  let unclaimed = 0;
  if (previous) {
    let released: ZoneShape[] = [];
    try {
      released = fromPieces(polygonClipping.difference(toPoly(previous), toPoly(next)));
    } catch {
      released = [];
    }
    for (const piece of released) {
      if (shapeArea(piece) <= SHARED_GROUND_EPSILON) continue;
      let best: { n: (typeof eligible)[number]; edge: number } | null = null;
      for (const n of eligible) {
        if (!shapesTouch(n.shape, piece)) continue;
        const edge = sharedEdgeLength(piece, n.shape);
        if (edge > 0 && (!best || edge > best.edge)) best = { n, edge };
      }
      if (!best) {
        unclaimed += 1;
        continue;
      }
      let grown: ZoneShape[];
      try {
        grown = fromPieces(polygonClipping.union(toPoly(best.n.shape), toPoly(piece)));
      } catch {
        unclaimed += 1;
        continue;
      }
      if (grown.length !== 1 || validRing(grown[0]!.boundary)) {
        unclaimed += 1;
        continue;
      }
      best.n.shape = grown[0]!;
      const prior = moves.get(best.n.row.id);
      moves.set(best.n.row.id, {
        id: best.n.row.id,
        name: best.n.row.name,
        boundary: grown[0]!.boundary,
        holes: grown[0]!.holes,
        change: prior ? 'pushed and pulled' : 'pulled',
        droppedPieces: prior?.droppedPieces ?? 0,
      });
      if (!pulled.includes(best.n.row.name)) pulled.push(best.n.row.name);
    }
  }

  const sentences: string[] = [];
  if (pushed.length > 0) {
    sentences.push(
      `Pushed ${nameList(pushed)} back — ${pushed.length === 1 ? 'its' : 'their'} boundary now runs along yours.`,
    );
  }
  if (cutUp.length > 0) {
    sentences.push(
      `That cut ${nameList(cutUp)} into pieces and only the largest ${cutUp.length === 1 ? 'was' : 'were'} kept.`,
    );
  }
  if (pulled.length > 0) {
    sentences.push(
      `${nameList(pulled)} followed you: the ground you released went to ${pulled.length === 1 ? 'it' : 'them'}.`,
    );
  }
  if (unclaimed > 0) {
    sentences.push('Ground you released beside no zone stays unassigned.');
  }
  if (keptHoles.length < (previous?.holes.length ?? 0)) {
    sentences.push('A cut-out the new ring no longer holds is gone with it.');
  }

  return {
    boundary: candidate,
    holes: keptHoles,
    neighbours: [...moves.values()],
    notice: sentences.length > 0 ? sentences.join(' ') : null,
    error: null,
  };
}

export type CutOut = {
  /** The zone after the cut, or null with `error`. */
  remaining: ZoneShape | null;
  /** The ground taken out — what a new zone would be given, exactly. */
  removed: ZoneShape | null;
  /** True when the cut opened a hole rather than a notch at the edge. */
  asHole: boolean;
  /** Pieces of the zone the cut severed and threw away; the largest was kept. */
  droppedPieces: number;
  notice: string | null;
  error: string | null;
};

const cutError = (error: string): CutOut => ({ remaining: null, removed: null, asHole: false, droppedPieces: 0, notice: null, error });

/**
 * Take a piece out of one zone.
 *
 * The removed ground is the ring intersected with the zone — never more than
 * the zone held — and the zone keeps the rest: a notch where the ring reached
 * its edge, a hole where it did not. A cut that touches none of the zone, or
 * all of it, is refused by name. What comes back as `removed` is the shape a
 * new zone is created with when the operator asks for one, so the new zone
 * and the hole fit exactly.
 */
export function cutOut(ring: LatLng[], target: ZoneShape, targetName: string): CutOut {
  const problem = validRing(ring);
  if (problem) return cutError(problem);
  const cutter: ZoneShape = { boundary: ring, holes: [] };
  let removedPieces: ZoneShape[];
  let remainingPieces: ZoneShape[];
  try {
    removedPieces = fromPieces(polygonClipping.intersection(toPoly(target), toPoly(cutter)));
    remainingPieces = fromPieces(polygonClipping.difference(toPoly(target), toPoly(cutter)));
  } catch {
    return cutError(`That cut could not be taken out of ${targetName} — redraw it.`);
  }
  if (removedPieces.length === 0 || shapeArea(removedPieces[0]!) <= SHARED_GROUND_EPSILON) {
    return cutError(`That cut lies outside ${targetName} — draw it over the ground to take out.`);
  }
  if (remainingPieces.length === 0 || shapeArea(remainingPieces[0]!) <= SHARED_GROUND_EPSILON) {
    return cutError(`That would take all of ${targetName}. Delete the zone instead.`);
  }
  const remaining = remainingPieces[0]!;
  const spoiled = validRing(remaining.boundary);
  if (spoiled) return cutError(`Cutting that out of ${targetName} leaves a shape that cannot be stored: ${spoiled}`);
  const removed = removedPieces[0]!;
  const asHole = remaining.holes.length > target.holes.length;
  const before = shapeArea(target);
  const share = before > 0 ? Math.round((shapeArea(removed) / before) * 100) : 0;
  const sentences = [
    `Takes about ${Math.max(1, share)}% of ${targetName} out${asHole ? ' as a hole in it' : ' at its edge'}.`,
  ];
  if (remainingPieces.length > 1) {
    sentences.push(
      `That cuts ${targetName} into ${remainingPieces.length} pieces and only the largest is kept.`,
    );
  }
  if (removedPieces.length > 1) {
    sentences.push('The ring reaches ground the zone holds in more than one place; the largest piece is what is taken out.');
  }
  return {
    remaining,
    removed,
    asHole,
    droppedPieces: remainingPieces.length - 1,
    notice: sentences.join(' '),
    error: null,
  };
}

// =============================================================================
// Split a zone with a line (2026-09-16)
//
// The operator's ask: draw a path across a zone and both halves become zones.
//
// A line is not a shape, and polygon-clipping only knows shapes, so the work
// here is turning the line into TWO shapes that together cover everything and
// share only the line — then each half is simply zone ∩ side. The line is
// EXTENDED first: its first segment is run backwards and its last segment
// forwards until each leaves a box drawn well around the zone and the line,
// so a line started or ended short of the edge still cuts the whole way
// through (nobody clicks exactly on a boundary, and a short stroke across the
// middle is what an operator actually draws). One side is the extended line
// closed along the box's perimeter one way round; the other is the same line
// closed the other way round. Both are plain rings. The zone's holes fall
// wherever they fall — whole into one half, or as notches on both — because
// the intersection is taken against the zone WITH its holes.
// =============================================================================

export type ZoneSplit = {
  /** The two halves, larger ground first, or null with `error`. */
  pieces: [ZoneShape, ZoneShape] | null;
  error: string | null;
  notice: string | null;
  /** Pieces thrown away because a side of the line met the zone in more than one place; the largest on each side was kept. */
  droppedPieces: number;
};

const splitError = (error: string): ZoneSplit => ({ pieces: null, error, notice: null, droppedPieces: 0 });

/** An axis-aligned box whose perimeter can be walked: 0 south (lng rising), 1 east (lat rising), 2 north (lng falling), 3 west (lat falling). */
type Box = { minLat: number; maxLat: number; minLng: number; maxLng: number };

/** Where a ray from a point INSIDE the box leaves it, and by which edge. */
function rayExit(box: Box, from: LatLng, dir: LatLng): { point: LatLng; edge: number } {
  const [lat, lng] = from;
  const [dLat, dLng] = dir;
  let best = { t: Infinity, edge: -1 };
  const consider = (t: number, edge: number) => {
    if (t > 0 && t < best.t) best = { t, edge };
  };
  if (dLat < 0) consider((box.minLat - lat) / dLat, 0);
  if (dLng > 0) consider((box.maxLng - lng) / dLng, 1);
  if (dLat > 0) consider((box.maxLat - lat) / dLat, 2);
  if (dLng < 0) consider((box.minLng - lng) / dLng, 3);
  // Clamped: float drift can put the hit a hair outside, and the perimeter walk needs it ON the edge.
  const point: LatLng = [
    Math.min(box.maxLat, Math.max(box.minLat, lat + best.t * dLat)),
    Math.min(box.maxLng, Math.max(box.minLng, lng + best.t * dLng)),
  ];
  return { point, edge: best.edge };
}

/** Distance along the perimeter walk, from the south-west corner, of a point on the given edge. */
function perimeterPosition(box: Box, point: LatLng, edge: number): number {
  const w = box.maxLng - box.minLng;
  const h = box.maxLat - box.minLat;
  switch (edge) {
    case 0:
      return point[1] - box.minLng;
    case 1:
      return w + (point[0] - box.minLat);
    case 2:
      return w + h + (box.maxLng - point[1]);
    default:
      return 2 * w + h + (box.maxLat - point[0]);
  }
}

/**
 * The corners of the box met walking its perimeter from `fromPos` to `toPos`
 * — forwards (the edge order above) or backwards — in the order they are met.
 */
function cornersBetween(box: Box, fromPos: number, toPos: number, forwards: boolean): LatLng[] {
  const w = box.maxLng - box.minLng;
  const h = box.maxLat - box.minLat;
  const perimeter = 2 * (w + h);
  const corners: Array<{ pos: number; point: LatLng }> = [
    { pos: 0, point: [box.minLat, box.minLng] },
    { pos: w, point: [box.minLat, box.maxLng] },
    { pos: w + h, point: [box.maxLat, box.maxLng] },
    { pos: 2 * w + h, point: [box.maxLat, box.minLng] },
  ];
  // How far along the walk each thing is, measured from `fromPos` in the walk's direction.
  const along = (pos: number) => (forwards ? pos - fromPos + perimeter : fromPos - pos + perimeter) % perimeter;
  const span = along(toPos);
  return corners
    .map((c) => ({ ...c, at: along(c.pos) }))
    .filter((c) => c.at > 0 && c.at < span)
    .sort((a, b) => a.at - b.at)
    .map((c) => c.point);
}

/**
 * Split one zone along a line the operator drew across it.
 *
 * The line is a polyline of two or more points, extended at both ends (see
 * above) so it need not start or end outside the zone. Each half is the zone
 * intersected with one side of the extended line, holes and all; the halves
 * come back larger first. A side that met the zone in more than one place
 * keeps its largest piece and the notice says so. A line that leaves the
 * whole zone on one side does not split it and is refused by name.
 */
export function splitZone(line: LatLng[], target: ZoneShape, targetName: string): ZoneSplit {
  if (line.length < 2) return splitError('A split line needs at least two points.');
  for (const [lat, lng] of line) {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return splitError('A split line point is not a coordinate.');
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return splitError('A split line point is off the map.');
  }
  // A double click lays the same point twice; a zero-length segment has no direction to extend.
  const path: LatLng[] = [];
  for (const p of line) {
    const last = path[path.length - 1];
    if (!last || last[0] !== p[0] || last[1] !== p[1]) path.push(p);
  }
  if (path.length < 2) return splitError('A split line needs two different points.');

  // A box around the zone AND the line, padded by half the larger dimension:
  // every point of the line is then strictly inside it, so each extension
  // leaves it exactly once, and the box edges sit well clear of the zone.
  let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
  for (const [lat, lng] of [...target.boundary, ...path]) {
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
  }
  const pad = Math.max(maxLat - minLat, maxLng - minLng, 1e-6) * 0.5;
  const box: Box = {
    minLat: Math.max(-90, minLat - pad),
    maxLat: Math.min(90, maxLat + pad),
    minLng: Math.max(-180, minLng - pad),
    maxLng: Math.min(180, maxLng + pad),
  };

  const first = path[0]!;
  const second = path[1]!;
  const last = path[path.length - 1]!;
  const beforeLast = path[path.length - 2]!;
  const entry = rayExit(box, first, [first[0] - second[0], first[1] - second[1]]);
  const exit = rayExit(box, last, [last[0] - beforeLast[0], last[1] - beforeLast[1]]);
  if (entry.edge < 0 || exit.edge < 0) return splitError('A split line needs two different points.');
  const entryPos = perimeterPosition(box, entry.point, entry.edge);
  const exitPos = perimeterPosition(box, exit.point, exit.edge);
  const acrossTheZone = `That line does not cut through ${targetName} — draw it across from one edge to the other.`;
  if (entryPos === exitPos) return splitError(acrossTheZone);

  // The extended line, closed one way round the box and the other way round.
  const cut: LatLng[] = [entry.point, ...path, exit.point];
  const sideA: LatLng[] = [...cut, ...cornersBetween(box, exitPos, entryPos, true)];
  const sideB: LatLng[] = [...cut, ...cornersBetween(box, exitPos, entryPos, false)];
  // A line that crosses itself, or whose extension crosses it, has no two sides.
  if (validRing(sideA) || validRing(sideB)) {
    return splitError(`That line doubles back across itself — draw it across ${targetName} from one edge to the other.`);
  }

  let piecesA: ZoneShape[];
  let piecesB: ZoneShape[];
  try {
    piecesA = fromPieces(polygonClipping.intersection(toPoly(target), [closeRing(sideA)]));
    piecesB = fromPieces(polygonClipping.intersection(toPoly(target), [closeRing(sideB)]));
  } catch {
    return splitError(`${targetName} could not be split along that line — redraw it.`);
  }
  const hasGround = (pieces: ZoneShape[]) => pieces.length > 0 && shapeArea(pieces[0]!) > SHARED_GROUND_EPSILON;
  if (!hasGround(piecesA) || !hasGround(piecesB)) return splitError(acrossTheZone);

  const [larger, smaller] = shapeArea(piecesA[0]!) >= shapeArea(piecesB[0]!) ? [piecesA, piecesB] : [piecesB, piecesA];
  for (const half of [larger[0]!, smaller[0]!]) {
    const spoiled = validRing(half.boundary);
    if (spoiled) return splitError(`Splitting ${targetName} along that line leaves a shape that cannot be stored: ${spoiled}`);
  }

  const before = shapeArea(target);
  const share = (s: ZoneShape) => (before > 0 ? Math.max(1, Math.round((shapeArea(s) / before) * 100)) : 0);
  const sentences = [`Splits ${targetName} into two pieces of about ${share(larger[0]!)}% and ${share(smaller[0]!)}%.`];
  const splintered = [larger, smaller].filter((side) => side.length > 1);
  if (splintered.length === 2) {
    sentences.push('Both sides of the line came out in more than one piece; only the largest on each is kept.');
  } else if (splintered.length === 1) {
    sentences.push(`One side of the line came out in ${splintered[0]!.length} pieces; only the largest is kept.`);
  }
  return {
    pieces: [larger[0]!, smaller[0]!],
    error: null,
    notice: sentences.join(' '),
    droppedPieces: larger.length - 1 + (smaller.length - 1),
  };
}

// --- distances along the ground ----------------------------------------------

/**
 * Everything above treats coordinates as planar, which is exact enough for
 * "do these shapes overlap". A fibre route's LENGTH is a different question:
 * it feeds a link budget in dB per kilometre, so it has to be metres on the
 * ground, not degrees. The great-circle distance (haversine on a mean-radius
 * sphere) is within 0.3% of the ellipsoid at any distance a PON reaches —
 * closer than a fibre laid along a road can be measured from a map anyway.
 */
export const EARTH_RADIUS_M = 6_371_008.8;

/** Great-circle distance between two [lat, lng] points, in metres. */
export function distanceMetres(a: LatLng, b: LatLng): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(b[0] - a[0]);
  const dLng = toRad(b[1] - a[1]);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a[0])) * Math.cos(toRad(b[0])) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * The length of a polyline of [lat, lng] points, in metres, rounded to a
 * decimetre. This is what `fibre_routes.length_m` holds: the API computes it
 * from the stored path on every write and never takes a length from a client,
 * and the map shows the same number live while the route is being laid.
 * Fewer than two points is no distance at all.
 */
export function pathLengthMetres(path: LatLng[]): number {
  let total = 0;
  for (let i = 1; i < path.length; i++) total += distanceMetres(path[i - 1]!, path[i]!);
  return Math.round(total * 10) / 10;
}
